import React, { useState, createRef, useCallback, useEffect } from "react";
import PersonalDataUpdateOcrWebcam from "./PersonalDataUpdateOcrWebcam/PersonalDataUpdateOcrWebcam";
import PersonalDataUpdateOcrActions from "./PersonalDataUpdateOcrActions/PersonalDataUpdateOcrActions";
import { sendImageToOcr } from "../../../../../functions/api";
import { convertBase64ToBlob } from "../../../../../functions/utils";
import {
  UPDATE_TYPES,
  OCR_STATUS_TYPES,
  OCR_ERROR_MESSAGES,
  UPLOAD_ERROR_MESSAGES,
  FILE_TYPES,
} from "./dataUpdateConstants";
import { AppContext } from "../../../../core";
import { getOcrData } from "../ocrData";

export const PersonalDataUpdateOcrUpload = ({ setOcrState, setOcrResult, setFailedOcrCount, ocrResult, failedOcrCount }) => {
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState("");
  const [capturedImage, setCapturedImage] = useState("");
  const [ocrStatus, setOcrStatus] = useState({
    type: OCR_STATUS_TYPES.DEFAULT,
    message: "",
  });
  const [updateType, setUpdateType] = useState("");
  const { resolveScreenAction, setApplicationError } = React.useContext(AppContext);
  const webcamRef = createRef(null);
  const hiddenFileInput = createRef(null);

  useEffect(
    () => {
      if (ocrStatus.type === OCR_STATUS_TYPES.SUCCESS) {
        setOcrState("Review");
      } else if (ocrStatus.type === OCR_STATUS_TYPES.ACCEPTED) {
        // Temporary measure due frequent failures from the OCR tool.
        // TODO: remove this logic once OCR is more robust.
        const ocrData = getOcrData(ocrResult);
        ocrData.postalCode = "";
        resolveScreenAction(ocrData)
          .catch((e) => {
            setApplicationError();
          });
      }
    },
    [ocrStatus, setOcrState]
  );

  const uploadHandler = (image) => {
    setLoading(true);

    sendImageToOcr(image.imageSource, failedOcrCount)
      .then((response) => {
        response.data.image = image.imageSource;
        setOcrResult(response.data);
        let statusType = OCR_STATUS_TYPES.SUCCESS;
        if (response.status === 202) { statusType = OCR_STATUS_TYPES.ACCEPTED }
        setOcrStatus((prevStatus) => ({
          ...prevStatus,
          type: statusType,
        }));
      })
      .catch((err) => {
        if (err?.response?.status === 400) {
          const message = err.response.data.errorCode;
          const isBusinessValidation = err.response.data.isBusinessValidation;
          setOcrStatus({
            type: OCR_STATUS_TYPES.ERROR,
            message: OCR_ERROR_MESSAGES[message],
          });
          if (!isBusinessValidation) {
            setFailedOcrCount((prevCount) => prevCount + 1);
          }
          setLoading(false);
          return;
        }
        setApplicationError();
      });
  };

  const isFileSizeValid = (file) => {
    const maxSizeAllowed = 5;
    const fileSizeInMb = (file.size / (1024 * 1024)).toFixed(2);

    return fileSizeInMb > maxSizeAllowed ? false : true;
  };

  const isFileTypeValid = (file) => {
    return FILE_TYPES.includes(file.type) ? true : false;
  };

  const uploadFileChangeHandler = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (!isFileTypeValid(file)) {
        setOcrStatus({
          type: OCR_STATUS_TYPES.ERROR,
          message: UPLOAD_ERROR_MESSAGES["INVALID_FILE_TYPE"],
        });

        return;
      }

      if (!isFileSizeValid(file)) {
        setOcrStatus({
          type: OCR_STATUS_TYPES.ERROR,
          message: UPLOAD_ERROR_MESSAGES["INVALID_FILE_SIZE"],
        });

        return;
      }

      setUploadedFile(file);
      setUpdateType(UPDATE_TYPES.UPLOAD);
      uploadHandler({ imageSource: file });
    }
  };

  const captureImageHandler = useCallback((e) => {
    e.preventDefault();

    if (!webcamRef.current || !webcamRef.current.getScreenshot()) {
      alert("Nu s-a putut activa camera. Vă rugam reîncercaţi.");
      return;
    }

    const capturedImageBase64 = webcamRef.current.getScreenshot();

    const blob = convertBase64ToBlob(capturedImageBase64);

    if (!isFileSizeValid(blob)) {
      setOcrStatus({
        type: OCR_STATUS_TYPES.ERROR,
        message: UPLOAD_ERROR_MESSAGES["INVALID_FILE_SIZE"],
      });

      return;
    }

    setCapturedImage(capturedImageBase64);
    setUpdateType(UPDATE_TYPES.IMAGE);
    uploadHandler({
      imageSource: blob,
    });
  });

  const uploadFileClickHandler = (e) => {
    hiddenFileInput.current.click();
  };

  const retryClickHandler = () => {
    setUpdateType("");
    setUploadedFile("");
    setCapturedImage("");
    setOcrStatus({
      type: OCR_STATUS_TYPES.DEFAULT,
      message: "",
    });
  };

  const showWebcam = ocrStatus.type === OCR_STATUS_TYPES.CAPTURE ? true : false;

  return (
    <>
      <h3 className="text-orange">Actualizare date Carte de Identitate</h3>
      <p>
        <strong>Fotografiază</strong>-ți cartea de identitate folosind butonul
        de mai jos sau <strong>încarcă</strong> o poză cu documentul solicitat.
        Poziţionează cartea de identitate în faţa camerei astfel încât să fie
        încadrată corect în chenarul afişat, se văd toate marginile, CI-ul este
        drept şi nu este fotografiat din lateral sau cu pete de lumină. Singurul
        document acceptat este cartea de identitate emisă de statul român.
      </p>

      {showWebcam && (
        <PersonalDataUpdateOcrWebcam
          capturedImage={capturedImage}
          ref={webcamRef}
        />
      )}

      <PersonalDataUpdateOcrActions
        captureImageHandler={captureImageHandler}
        loading={loading}
        uploadFileClickHandler={uploadFileClickHandler}
        uploadFileChangeHandler={uploadFileChangeHandler}
        ref={hiddenFileInput}
        uploadedFile={uploadedFile}
        ocrStatus={ocrStatus}
        retryClickHandler={retryClickHandler}
        updateType={updateType}
        setOcrStatus={setOcrStatus}
      />

      <p className="font-italic">
        Dimensiunea maximă a fișierelor încărcate este de 5MB. Formatele
        acceptate sunt: .pdf, .jpeg, .png, .tiff, .bmp.
      </p>
    </>
  );
};
